import { Actions, createEffect, ofType } from '@ngrx/effects'
import {
  ComponentModifier,
  ScenarioModifierType,
} from '@app/features/company-management/models/component-modifier.model'
import { SNACKBAR_LENGTH_LONG, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'

import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs'
import { isInteger, isNil, isNumber } from 'lodash'

import { EnvService } from '@envs/env-service'
import { FormulaService } from '@app/shared/services/formula.service'
import { Injectable } from '@angular/core'
import { InvalidModifiersPayload } from '@app/features/scope-overview/model/scope-configuration/invalid-modifiers-payload.model'
import { KeyValue } from '@app/core/types/key-value.type'
import { MatDialog } from '@angular/material/dialog'
import { ScopeConfigurationActions } from '../actions/scope-configuration.actions'
import { ScopeConfigurationSelectors } from '../selectors/scope-configuration.selectors'
import { ScopeConfigurationService } from '@app/features/scope-overview/service/scope-configuration.service'
import { ScopeOverviewNavigationService } from '@app/features/scope-overview/service/scope-overview-navigation.service'
import { ScopeOverviewSelectors } from '../selectors/scope-overview.selector'
import { ScopeOverviewTab } from '@app/features/scope-overview/enum/scope-overview-tab.enum'
import { ScopeUiModalComponent } from '@app/shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { Store } from '@ngrx/store'
import { getConfigurationWithoutAddons } from '../../utils/get-configuration-without-addons.util'
import { ScopeTabService } from '../../service/scope-tab.service'

@Injectable()
export class ScopeConfigurationEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private formulaService: FormulaService,
    private scopeConfigurationService: ScopeConfigurationService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    private scopeOverviewNavigationService: ScopeOverviewNavigationService,
    private envService: EnvService,
    private scopeTabService: ScopeTabService
  ) {}

  saveConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopeConfigurationActions.saveConfiguration),
      switchMap(({ scopeId, configuration }) =>
        this.scopeConfigurationService.saveConfiguration(scopeId, configuration).pipe(
          map(() => ScopeConfigurationActions.saveConfigurationSuccess({ configuration })),
          catchError((error: any) => {
            console.error(error)
            this.snackbarService.showSnackbar(
              'An error occurred while saving the configuration form',
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopeConfigurationActions.saveConfigurationFail({ error }))
          })
        )
      )
    )
  )

  submitForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopeConfigurationActions.submitForm),
      withLatestFrom(
        this.store$.select(ScopeOverviewSelectors.selectScenario),
        this.store$.select(ScopeConfigurationSelectors.selectConfiguration)
      ),
      switchMap(([{ scenarioId, configuration }, scenario]) => {
        const rawConfiguration = getConfigurationWithoutAddons(scenario, configuration, this.formulaService)

        return this.scopeConfigurationService
          .saveConfiguration(scenarioId, rawConfiguration)
          .pipe(map(() => ScopeConfigurationActions.getModifiers({ scenarioId: scenario.id })))
      })
    )
  )

  getModifiers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopeConfigurationActions.getModifiers),
      switchMap(({ scenarioId }) =>
        this.scopeConfigurationService.getModifiers(scenarioId).pipe(
          map((modifiers: ComponentModifier[]) => ScopeConfigurationActions.getModifiersSuccess({ modifiers })),
          catchError((error) => {
            console.error(error)
            this.snackbarService.showSnackbar(
              'An error occurred while loading modifiers',
              SNACKBAR_LENGTH_LONG,
              SnackbarEventType.ERROR
            )
            return of(ScopeConfigurationActions.getModifiersFail({ error }))
          })
        )
      )
    )
  )

  prepareConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopeConfigurationActions.getModifiersSuccess),
      map(({ modifiers }) => {
        const { invalidPriceModifiers, invalidQuantityModifiers, modifierResults }: InvalidModifiersPayload =
          this.getModifierResults(modifiers)

        if (invalidPriceModifiers?.length || invalidQuantityModifiers?.length)
          return ScopeConfigurationActions.showInvalidModifiersModal({
            invalidPriceModifiers,
            invalidQuantityModifiers,
            modifierResults,
          })

        this.snackbarService.showSnackbar('Scope is being configured...', SNACKBAR_LENGTH_LONG, SnackbarEventType.INFO)

        return ScopeConfigurationActions.submitConfiguration({ modifierResults })
      })
    )
  )

  submitConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScopeConfigurationActions.submitConfiguration),
      withLatestFrom(this.store$.select(ScopeOverviewSelectors.selectCurrentScope)),
      switchMap(([payload, currentScope]) => {
        this.scopeTabService.resetTableStates()

        return this.scopeConfigurationService
          .submitConfiguration(currentScope.identity.id, payload.modifierResults)
          .pipe(
            map(() => {
              this.snackbarService.showPrioritisedSnackbar(
                'Your scope is ready! Loading...',
                SNACKBAR_LENGTH_LONG,
                SnackbarEventType.SUCCESS
              )

              return ScopeConfigurationActions.submitConfigurationSuccess({ currentScope })
            }),
            catchError((error: any) => {
              console.error(error)
              this.snackbarService.showSnackbar(
                'An error occurred while submitting the configuration form',
                SNACKBAR_LENGTH_LONG,
                SnackbarEventType.ERROR
              )
              return of(ScopeConfigurationActions.submitConfigurationFail({ error }))
            })
          )
      })
    )
  )

  reloadConfiguration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScopeConfigurationActions.submitConfigurationSuccess),
        map(({ currentScope }) => {
          this.scopeOverviewNavigationService.selectTabAndReload(ScopeOverviewTab.SCOPE, currentScope.identity.id)
        })
      ),
    { dispatch: false }
  )

  showInvalidModifiersModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ScopeConfigurationActions.showInvalidModifiersModal),
        map(({ invalidPriceModifiers, invalidQuantityModifiers, modifierResults }) => {
          let body = ''

          if (invalidPriceModifiers.length) {
            const title = this.getInvalidModifiersTitle(invalidPriceModifiers, 'price')
            const modifiers = this.getInvalidModifiers(invalidPriceModifiers, modifierResults)

            body += title + modifiers
          }
          if (invalidQuantityModifiers.length) {
            const title = this.getInvalidModifiersTitle(invalidQuantityModifiers, 'quantity', true)
            const modifiers = this.getInvalidModifiers(invalidQuantityModifiers, modifierResults)

            body += (body.length ? '\n' : '') + title + modifiers
          }

          const invalidModifiers = invalidPriceModifiers.length + invalidQuantityModifiers.length
          const dialog = this.dialog.open(ScopeUiModalComponent, {
            data: {
              title: `Invalid modifier value${invalidModifiers > 1 ? 's' : ''}`,
              body,
              confirmText: 'Close',
              confirmCallback: () => dialog.close(),
              inlineHtmlBody: true,
            },
          })
        })
      ),
    { dispatch: false }
  )

  private getModifierResults(modifiers: ComponentModifier[] = []): InvalidModifiersPayload {
    const modifierResults: KeyValue = {}
    modifiers.forEach((m) => (modifierResults[m.id] = this.formulaService.calculate(m.formula) as number))

    let invalidQuantityModifiers = undefined
    let invalidPriceModifiers = undefined
    if (this.envService.enableModifierValidation) {
      invalidPriceModifiers = modifiers.filter(
        (m) =>
          m.type === ScenarioModifierType.PRICE_MODIFIER &&
          ((!isNil(modifierResults[m.id]) && !isNumber(modifierResults[m.id])) || modifierResults[m.id] < 0)
      )
      invalidQuantityModifiers = modifiers.filter(
        (m) =>
          m.type === ScenarioModifierType.QUANTITY_MODIFIER &&
          ((!isNil(modifierResults[m.id]) && !isInteger(modifierResults[m.id])) || modifierResults[m.id] < 0)
      )
    }

    return { modifierResults, invalidPriceModifiers, invalidQuantityModifiers }
  }

  private getInvalidModifiers = (modifiers: ComponentModifier[], modifierResults: KeyValue) =>
    modifiers.map((m) => `\u2022 ${m.name}: ${modifierResults[m.id]}\n`).join('')

  private getInvalidModifiersTitle = (modifiers: ComponentModifier[], type: string, isInteger?: boolean) =>
    modifiers.length > 1
      ? `The following ${type} modifier values must be positive ${isInteger ? 'whole ' : ''}numbers:\n`
      : `The following ${type} modifier value must be a positive ${isInteger ? 'whole ' : ''}number:\n`
}
